import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Brain, Save, RefreshCw, Zap, Sparkles, Bot } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ── Model definitions ─────────────────────────────────────────────────────────

export type AIModelKey = 'haiku' | 'sonnet' | 'gpt-mini'

interface ModelDef {
  key: AIModelKey
  label: string
  provider: 'Anthropic' | 'OpenAI'
  description: string
  speed: 'Fastest' | 'Balanced' | 'Fast'
  costLabel: string
  badgeColor: string
  badgeText: string
  icon: React.FC<{ className?: string }>
}

const MODELS: ModelDef[] = [
  {
    key: 'haiku',
    label: 'Claude Haiku',
    provider: 'Anthropic',
    description: 'Instant responses for rule building, compose assist, and feed summaries. The lightest touch — great for everyday use.',
    speed: 'Fastest',
    costLabel: '~$0.25 / 1M tokens',
    badgeColor: 'bg-emerald-100 text-emerald-800',
    badgeText: 'Default · Cheapest',
    icon: Zap,
  },
  {
    key: 'sonnet',
    label: 'Claude Sonnet',
    provider: 'Anthropic',
    description: 'More nuanced writing, better rule suggestions, and richer feed digests. Worth the step-up for power users.',
    speed: 'Balanced',
    costLabel: '~$3 / 1M tokens',
    badgeColor: 'bg-blue-100 text-blue-800',
    badgeText: 'Recommended',
    icon: Sparkles,
  },
  {
    key: 'gpt-mini',
    label: 'GPT-4o mini',
    provider: 'OpenAI',
    description: "OpenAI's compact flagship model. A second opinion from a different AI engine — same tasks, different style.",
    speed: 'Fast',
    costLabel: '~$0.15 / 1M tokens',
    badgeColor: 'bg-purple-100 text-purple-800',
    badgeText: 'OpenAI engine',
    icon: Bot,
  },
]

// ── Component ─────────────────────────────────────────────────────────────────

interface AIAssistantSettingsProps {
  onSettingsChange?: (model: AIModelKey) => void
}

const AIAssistantSettings: React.FC<AIAssistantSettingsProps> = ({ onSettingsChange }) => {
  const { user } = useAuth()
  const [selectedModel, setSelectedModel] = useState<AIModelKey>('haiku')
  const [loadedModel, setLoadedModel]   = useState<AIModelKey>('haiku')   // last persisted value
  const [loading, setLoading]           = useState(true)
  const [saving, setSaving]             = useState(false)

  // ── Load current preference ─────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) { setLoading(false); return }

    supabase
      .from('user_preferences')
      .select('ai_preferred_model')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        const model = (data as any)?.ai_preferred_model as AIModelKey | null
        if (model && (['haiku', 'sonnet', 'gpt-mini'] as string[]).includes(model)) {
          setSelectedModel(model)
          setLoadedModel(model)
        }
        setLoading(false)
      })
  }, [user?.id])

  // ── Save preference ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!user?.id) return
    setSaving(true)
    try {
      const { error } = await supabase
        .from('user_preferences')
        .update({ ai_preferred_model: selectedModel } as any)
        .eq('user_id', user.id)

      if (error) throw error

      setLoadedModel(selectedModel)
      onSettingsChange?.(selectedModel)
      toast.success('AI model preference saved')
    } catch (err) {
      console.error('Failed to save AI model:', err)
      toast.error('Could not save preference — please try again')
    } finally {
      setSaving(false)
    }
  }

  const isDirty = selectedModel !== loadedModel

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-28 rounded-lg bg-muted" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Brain className="h-5 w-5 text-[#1A2E4A]" />
            AI Engine
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Choose which AI model powers your rule builder, compose assist, feed digest, and reply drafts.
            All models are called on-demand — you only pay when you use an AI feature.
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || !isDirty}
          size="sm"
          className="shrink-0 bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white"
        >
          {saving ? (
            <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          ) : (
            <Save className="h-3.5 w-3.5 mr-1.5" />
          )}
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {/* Model selector cards */}
      <div className="space-y-3">
        {MODELS.map((m) => {
          const Icon = m.icon
          const active = selectedModel === m.key
          return (
            <button
              key={m.key}
              onClick={() => setSelectedModel(m.key)}
              className={[
                'w-full text-left rounded-lg border p-4 transition-all',
                active
                  ? 'border-[#1A2E4A] bg-[#1A2E4A]/[0.04] ring-1 ring-[#1A2E4A]/20'
                  : 'border-border hover:border-[#1A2E4A]/40 hover:bg-muted/40',
              ].join(' ')}
            >
              <div className="flex items-start gap-3">
                {/* Radio indicator */}
                <div className={[
                  'mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors',
                  active ? 'border-[#1A2E4A]' : 'border-border',
                ].join(' ')}>
                  {active && <div className="h-2 w-2 rounded-full bg-[#1A2E4A]" />}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2">
                    <Icon className="h-4 w-4 text-[#1A2E4A]/70" />
                    <span className="font-medium text-sm">{m.label}</span>
                    <Badge className={`text-[10px] py-0 px-1.5 ${m.badgeColor}`}>
                      {m.badgeText}
                    </Badge>
                    {loadedModel === m.key && (
                      <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-green-300 text-green-700">
                        Active
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {m.description}
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-[11px] text-muted-foreground">
                      <span className="font-medium">Speed:</span> {m.speed}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      <span className="font-medium">Cost:</span> {m.costLabel}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      <span className="font-medium">By:</span> {m.provider}
                    </span>
                  </div>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      <Separator />

      {/* Info section */}
      <Card className="border-[#1A2E4A]/15 bg-[#1A2E4A]/[0.02]">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-medium">How SocialScoop uses AI</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-2 text-xs text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">Rule Builder</span> — describe a rule in plain language; AI converts it into keywords and settings.
          </p>
          <p>
            <span className="font-medium text-foreground">Compose Assist</span> — draft, improve, or generate hashtags for posts directly in the compose window.
          </p>
          <p>
            <span className="font-medium text-foreground">Feed Digest</span> — a warm 1–2 sentence summary of what's new in your feeds since you last visited.
          </p>
          <p>
            <span className="font-medium text-foreground">Reply Drafts</span> — three ready-to-send reply options (curious, perspective, supportive) for any post.
          </p>
          <p>
            <span className="font-medium text-foreground">Smart Rule Suggestions</span> — learns from posts you manually hide and suggests filters to automate that.
          </p>
          <Separator className="my-1" />
          <p className="text-[11px]">
            AI calls are made on demand only — never in the background. No content is stored by the AI provider beyond the current request.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

export default AIAssistantSettings
export type { AIModelKey }
